export type Permission =
    | "VIEW_DASHBOARD"
    | "VIEW_PII"
    | "EDIT_GROUP_DETAILS"
    | "MANAGE_FUNDS"
    | "VIEW_METRICS"
    | "EDIT_MEMBER_ROLES"
    | "VIEW_MEMBERS"
    | "MANAGE_TASKS"
    | "VIEW_TASKS"
    | "VIEW_BOARD_REPORTS";

export interface AssignmentContext {
    /** The API sends `assignment_id` (see `AssignmentInfo` in
     *  backend/app/api/routes/auth.py). This was declared as `id`, which is a
     *  field the payload has never carried — anything keying a list on it got
     *  `undefined` for every row. */
    assignment_id: string;
    role: string;
    entity_type: string;
    entity_id: number;
    title?: string; // Optional friendly name like "NYC Chapter"
    entity_name?: string;
}


/**
 * A live temporary grant letting someone help at ONE event.
 *
 * Deliberately kept apart from `assignments`: a helper is typically not a member
 * of the group (often not a member of CMT Association at all), and everything
 * that reads `assignments` — sidebars, role maps, the group directory, gigs
 * membership — must not treat them as one. Usually an empty array.
 */
export interface EventHelperGrant {
    meeting_id: string;
    entity_type: string;
    entity_id: number;
    meeting_title?: string;
    capabilities: string[];
    /** Derived from the event's dates, so it moves if the event is rescheduled. */
    access_until?: string;
}

export interface UserContext {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role: string; // sys_role (e.g. 'admin', 'superadmin', 'staff', 'leader', 'volunteer')
    assignments: AssignmentContext[];
    event_helper_grants?: EventHelperGrant[];
    community?: string;
    chapter?: string;
    is_profile_complete?: boolean;
    must_change_password?: boolean;
    is_test_user?: boolean;
    is_staff?: boolean;
    coc_current_version?: string;
    coc_accepted_version?: string;
    phone?: string;
    bio?: string;
    linkedin?: string;
    /** Discord link. Read-only here — only the OAuth callback can set it.
     *  `discord_id` is a 64-bit snowflake and must stay a string. */
    discord_id?: string | null;
    discord_username?: string | null;
    discord_verified_at?: string | null;
    discord_link_source?: string | null;
    profile_photo_url?: string;
    company?: string;
    job_title?: string;
    city?: string;
    country?: string;
    designations?: string; // CSV; CMT is computed live by the backend
    designation_other?: string;
    member_since?: string;
    member_status?: string; // "active" | "inactive"
    program_journey?: string;
    membership_journey?: string;
    gender_ai?: string;
    gender_self?: string;
}

/**
 * True when this user holds a live temporary helper grant on that event.
 *
 * Only ever widens access to the one event's working surfaces. It deliberately
 * does NOT feed `getViewerTier`, `hasPermission` or any group-scoped check — the
 * server is the real ceiling, and this is only here so the UI can stop showing a
 * non-member a "renew your membership" wall on an event they were invited to help
 * run.
 */
export function isEventHelperFor(user: UserContext | null, meetingId?: string | null): boolean {
    if (!user || !meetingId) return false;
    return (user.event_helper_grants || []).some((g) => g.meeting_id === meetingId);
}

/** The live helper grant for that event, if any. */
export function eventHelperGrant(user: UserContext | null, meetingId?: string | null): EventHelperGrant | null {
    if (!user || !meetingId) return null;
    return (user.event_helper_grants || []).find((g) => g.meeting_id === meetingId) || null;
}

/** Returns true when the user has an active membership and can use interactive features. */
export function isActiveMember(user: UserContext | null): boolean {
    if (!user) return false;
    const sysRole = (user.role || "").toLowerCase();
    if (sysRole === "admin" || sysRole === "superadmin" || user.is_staff) return true;
    return (user.member_status || "active") === "active";
}

// Global permissions for Staff/Admins that apply everywhere
const STAFF_GLOBAL_PERMISSIONS: Permission[] = [
    "VIEW_DASHBOARD",
    "VIEW_PII",
    "EDIT_GROUP_DETAILS",
    "EDIT_MEMBER_ROLES",
    "MANAGE_TASKS",
    "VIEW_METRICS",
    "VIEW_MEMBERS",
    "VIEW_TASKS",
    "VIEW_BOARD_REPORTS",
];

// Map a specific assignment role string to permissions
// We use lowercase keys to safely match `.toLowerCase()`
const ROLE_PERMISSION_MAP: Record<string, Permission[]> = {
    "chapter chair": ["VIEW_DASHBOARD", "VIEW_PII", "EDIT_GROUP_DETAILS", "MANAGE_FUNDS", "VIEW_METRICS", "EDIT_MEMBER_ROLES", "VIEW_MEMBERS", "MANAGE_TASKS", "VIEW_TASKS"],
    "vice chair": ["VIEW_DASHBOARD", "VIEW_PII", "VIEW_METRICS", "VIEW_MEMBERS", "MANAGE_TASKS", "VIEW_TASKS"],
    "staff liaison": ["VIEW_DASHBOARD", "VIEW_PII", "VIEW_METRICS", "VIEW_MEMBERS", "MANAGE_TASKS", "VIEW_TASKS"],
    "board liaison": ["VIEW_DASHBOARD", "VIEW_TASKS"],
    "secretary": ["VIEW_DASHBOARD", "VIEW_PII", "VIEW_MEMBERS", "MANAGE_TASKS", "VIEW_TASKS"],
    "volunteer": ["VIEW_DASHBOARD", "VIEW_MEMBERS", "VIEW_TASKS"],
};

/**
 * Evaluates whether a user has a specific permission, given their active context.
 */
export type ViewerTier = "admin" | "staff" | "leader" | "volunteer";

export function getViewerTier(user: UserContext | null): ViewerTier {
    if (!user) return "volunteer";
    const sys = (user.role || "").toLowerCase();
    if (sys === "admin" || sys === "superadmin") return "admin";
    if (user.is_staff || sys === "staff") return "staff";
    const leaderRoles = ["chair", "vice", "lead", "secretary", "liaison"];
    const isLeader = user.assignments?.some((a) => {
        const r = (a.role || "").toLowerCase();
        return leaderRoles.some((kw) => r.includes(kw));
    });
    if (isLeader) return "leader";
    return "volunteer";
}

/**
 * Returns true if a role label should be displayed in user profile headers/dropdowns.
 * Suppresses generic "leader" and "volunteer" labels to avoid confusion.
 */
export function shouldDisplayRole(role?: string | null): boolean {
    if (!role) return false;
    const r = role.toLowerCase().trim();
    return r !== "leader" && r !== "volunteer";
}

export function hasPermission(
    permission: Permission,
    user: UserContext | null,
    activeContext: AssignmentContext | null
): boolean {
    if (!user) return false;

    const sysRole = (user.role || "").toLowerCase();

    // 1. Admins have omnipotent access
    if (sysRole === "admin" || sysRole === "superadmin") return true;

    // 2. Only users with is_staff=true or role='staff' get broad global access
    if ((user.is_staff || sysRole === "staff") && STAFF_GLOBAL_PERMISSIONS.includes(permission)) return true;

    // 3. Fallback to active context
    // If there is no active context, test if there are any baseline permissions for the user's sys_role
    if (!activeContext) {
        if (sysRole === "volunteer") {
             // Let them view the dashboard and basic public things
             const basePerms: Permission[] = ["VIEW_DASHBOARD"];
             return basePerms.includes(permission);
        }
        return false;
    }

    const contextRole = (activeContext.role || "").toLowerCase();

    // Exact match or fallback partial match (e.g. "chair" matches "chapter chair")
    let grantedPerms: Permission[] = [];

    if (ROLE_PERMISSION_MAP[contextRole]) {
         grantedPerms = ROLE_PERMISSION_MAP[contextRole];
    } else {
         // Fuzzy match if someone is called "Committee Chair" instead of "Chapter Chair"
         if (contextRole.includes("chair") || contextRole.includes("lead")) {
              grantedPerms = ROLE_PERMISSION_MAP["chapter chair"];
         } else if (contextRole.includes("vice")) {
              grantedPerms = ROLE_PERMISSION_MAP["vice chair"];
         } else if (contextRole.includes("staff liaison")) {
              grantedPerms = ROLE_PERMISSION_MAP["staff liaison"];
         } else if (contextRole.includes("board liaison")) {
              grantedPerms = ROLE_PERMISSION_MAP["board liaison"];
         } else {
              // Default to basic volunteer permissions
              grantedPerms = ROLE_PERMISSION_MAP["volunteer"];
         }
    }

    return grantedPerms.includes(permission);
}
